import { useEffect, useRef, useState } from 'react';
import { googleAuthApi } from '../../api/apiServices';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let gsiScriptPromise = null;
function loadGsiScript() {
  if (gsiScriptPromise) return gsiScriptPromise;
  gsiScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return gsiScriptPromise;
}

/**
 * Renders Google's "Sign in with Google" button. Renders nothing if
 * VITE_GOOGLE_CLIENT_ID isn't configured, so the feature stays fully
 * optional and never breaks the login/signup pages.
 *
 * Props:
 *  - onSuccess(data): called with the backend's { accessToken, user } response
 *  - onError(message): called if the exchange with the backend fails
 */
export default function GoogleSignInButton({ onSuccess, onError }) {
  const buttonRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    let cancelled = false;

    loadGsiScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            try {
              const data = await googleAuthApi(response.credential);
              onSuccess?.(data);
            } catch (err) {
              onError?.(err?.response?.data?.message || 'Google sign-in failed. Please try again.');
            }
          },
        });

        if (buttonRef.current) {
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            width: 320,
            text: 'continue_with',
          });
        }
        setReady(true);
      })
      .catch(() => {
        onError?.('Could not load Google sign-in. Check your connection and try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [onSuccess, onError]);

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className="flex justify-center">
      <div ref={buttonRef} />
      {!ready && (
        <div className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50 animate-pulse" />
      )}
    </div>
  );
}
